import {
    ResultObj,
    allFetch,
} from "../Domain";

/**
 * @description 公司新建參數
 * @param {string} type 類型
 * @param {string} name 公司名稱
 * @param {string} code 公司代碼
 * @param {string} tax_id 統編號碼
 * @param {string} city 城市
 * @param {string} area 地區
 * @param {string} address 地址
 * @param {string} phone 電話
 * @param {string} url 網址
 * @param {string} note 備註
 * @param {string} transfer_account 匯款帳號
 * @param {string} start_date 起始年月
 * @param {string} howmany_month 每幾個月
 * @param {string} which_date 幾號通知
 * @param {string} howmany_beforeday 幾天前通知
 */
export interface CompanyCreateParams {
    type: string;
    name: string;
    code: string;
    tax_id: string;
    city: string;
    area: string;
    address: string;
    phone: string;
    url: string;
    note: string;
    transfer_account: string;
    start_date: string;
    howmany_month: string;
    which_date: string;
    howmany_beforeday: string;
}

/**
 * @description 公司新建
 */
export const CompanyCreate = async (
    obj: CompanyCreateParams
): Promise<ResultObj> => {
    return await allFetch("Company/CompanyCreate", obj);
};

/**
 * @description 公司細項查詢參數
 * @param {string} type 類別
 * @param {string} name 公司名稱
 * @param {string} code 公司代碼
 * @param {string} city 城市
 * @param {string} area 地區
 * @param {string} visible 是否可見
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface CompanySearchParams {
    type: string;
    name?: string;
    city?: string;
    area?: string;
    visible?: string;
    page: number;
    num_per_page: number;
}

/**
 * @description 公司細項查詢結果
 */
export interface CompanySearchResItem {
    address: string;
    area: string;
    city: string;
    code: string;
    company_id: string;
    howmany_beforeday: number;
    howmany_month: number;
    name: string;
    note: string;
    phone: string;
    start_date: string;
    tax_id: string;
    type: string;
    url: string;
    visible: string;
    which_date: number;
};

/**
 * @description 公司細項查詢
 */
export const CompanySearch = async (
    obj: CompanySearchParams
): Promise<ResultObj<CompanySearchResItem[]>> => {
    return await allFetch("Company/CompanySearch", obj);
};

/**
 * @description 公司修改參數
 * @param {string} company_id 公司流水號
 * @param {string} name 公司名稱
 * @param {string} code 公司代碼
 * @param {string} tax_id 統編號碼
 * @param {string} city 城市
 * @param {string} area 地區
 * @param {string} address 地址
 * @param {string} phone 電話
 * @param {string} url 網址
 * @param {string} note 備註
 * @param {string} visible 是否可見
 * @param {string} transfer_account 匯款帳號
 * @param {string} start_date 起始年月
 * @param {string} howmany_month 每幾個月
 * @param {string} which_date 幾號通知
 * @param {string} howmany_beforeday 幾天前通知
 */
export interface CompanyUpdateParams {
    company_id: string;
    name: string;
    code: string;
    tax_id: string;
    city: string;
    area: string;
    address: string;
    phone: string;
    url: string;
    note: string;
    visible: "Y" | "N";
    transfer_account: string;
    start_date: string;
    howmany_month: number;
    which_date: string;
    howmany_beforeday: number;
}

/**
 * @description 公司修改
 */
export const CompanyUpdate = async (
    obj: CompanyUpdateParams
): Promise<ResultObj> => {
    return await allFetch("Company/CompanyUpdate", obj);
};

/**
 * @description 公司詳細資訊查詢參數
 * @param {string} company_id 公司流水號
 */
export interface CompanyDetailParams {
    company_id: string;
}

export interface CompanyDetailResItem {
    address: string;
    area: string;
    city: string;
    code: string;
    company_id: string;
    howmany_beforeday: number;
    howmany_month: number;
    name: string;
    note: string;
    phone: string;
    start_date: string;
    tax_id: string;
    transfer_account: string;
    type: string;
    url: string;
    visible: "Y" | "N";
    which_date: string;
}

/**
 * @description 公司詳細資訊查詢
 */
export const CompanyDetail = async (
    obj: CompanyDetailParams
): Promise<ResultObj<CompanyDetailResItem[]>> => {
    return await allFetch("Company/CompanyDetail", obj);
};

/**
 * @description 聯絡人新建參數
 * @param {string} company_id 公司流水號
 * @param {string} name 聯絡人姓名
 * @param {string} phone 聯絡人電話
 * @param {string} gender 聯絡人性別
 * @param {string} position 聯絡人職稱
 * @param {string} email 聯絡人信箱
 */
export interface CompanyContacterCreateParams {
    company_id: string;
    name: string;
    phone: string;
    gender: string;
    position: string;
    email: string;
}

/**
 * @description 聯絡人新建
 */
export const CompanyContacterCreate = async (
    obj: CompanyContacterCreateParams
): Promise<ResultObj> => {
    return await allFetch("Company/CompanyContacterCreate", obj);
};

/**
 * @description 聯絡人查詢參數
 * @param {string} company_id 公司流水號
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface CompanyContacterSearchParams {
    company_id: string;
    page: number;
    num_per_page: number;
}

export interface CompanyContacterSearchResItem {
    cc_id: string,
    company_id: string;
    email: string;
    gender: string;
    name: string;
    phone: string;
    position: string;
}

/**
 * @description 聯絡人查詢
 */
export const CompanyContacterSearch = async (
    obj: CompanyContacterSearchParams
): Promise<ResultObj<CompanyContacterSearchResItem[]>> => {
    return await allFetch("Company/CompanyContacterSearch", obj);
};

/**
 * @description 聯絡人修改參數
 * @param {string} cc_id 公司聯絡人流水號
 * @param {string} company_id 公司流水號
 * @param {string} name 聯絡人姓名
 * @param {string} phone 聯絡人電話
 * @param {string} gender 聯絡人性別
 * @param {string} position 聯絡人職稱
 * @param {string} email 聯絡人信箱
 */
export interface CompanyContacterUpdateParams {
    cc_id: string;
    company_id: string;
    name: string;
    phone: string;
    gender: string;
    position: string;
    email: string;
}

/**
 * @description 聯絡人修改
 */
export const CompanyContacterUpdate = async (
    obj: CompanyContacterUpdateParams
): Promise<ResultObj> => {
    return await allFetch("Company/CompanyContacterUpdate", obj);
};

/**
 * @description 聯絡人刪除參數
 * @param {string} cc_id 公司聯絡人流水號
 */
export interface CompanyContacterDeleteParams {
    cc_id: string;
}

/**
 * @description 聯絡人刪除
 */
export const CompanyContacterDelete = async (
    obj: CompanyContacterDeleteParams
): Promise<ResultObj> => {
    return await allFetch("Company/CompanyContacterDelete", obj);
};

/**
 * @description 聯絡人詳細資訊查詢參數
 * @param {string} cc_id 公司聯絡人流水號
 */
export interface CompanyContacterDetailParams {
    cc_id: string;
}

export interface CompanyContacterDetailResItem {
    cc_id: string;
    company_id: string;
    email: string;
    gender: string;
    name: string;
    phone: string;
    position: string;
}
/**
 * @description 聯絡人詳細資訊查詢
 */
export const CompanyContacterDetail = async (
    obj: CompanyContacterDetailParams
): Promise<ResultObj> => {
    return await allFetch("Company/CompanyContacterDetail", obj);
};